/**
 * Client-side mirror of `utils/compliance_hashing.py:compute_compliance_content_hash`.
 *
 * The canonical implementation lives on the server; this file is the JS
 * binding the advisor-side compliance chip uses to flip its "changed
 * since review" state the moment the advisor edits subject/body, without
 * waiting for the next contacts GET to re-stamp `is_stale` server-side.
 *
 * Byte-for-byte compatibility with the Python version is the contract:
 *
 *     canonical = `${subject || ''}\n${body || ''}`
 *     sha256(canonical, utf-8) -> lowercase hex
 *
 * Anyone changing this formula MUST change both sides together. The
 * Python module's docstring is the spec; this module is a passive port.
 *
 * Web Crypto (`crypto.subtle.digest`) is the only async dependency and
 * is available in every browser we target plus jsdom >= 22. Callers that
 * hit an environment without it should treat a rejected promise as "fall
 * back to the server-stamped `is_stale`" and render the server verdict
 * unchanged — the overlay is an enhancement, not a correctness barrier.
 */

/**
 * Match Python's falsy-coerce-to-empty-string behavior from the f-string
 * expression `f"{subject or ''}\n{body or ''}"`:
 *
 *   None  -> ''
 *   ''    -> ''
 *   0     -> ''         (Python treats 0 as falsy here; so do we)
 *   False -> ''
 *   'hi'  -> 'hi'
 *
 * Non-string truthy values (rare; the API returns strings or null) are
 * coerced via `String(...)` so the formula never throws on unexpected
 * upstream shapes.
 */
const toCanonicalString = (value) => {
  if (!value) return '';
  return typeof value === 'string' ? value : String(value);
};

/**
 * SHA-256 hex digest of the canonical `(subject, body)` pair.
 *
 * @param {unknown} subject  Current subject value (string | null | undefined).
 * @param {unknown} body     Current body value (string | null | undefined).
 * @returns {Promise<string>}  Lowercase hex digest, 64 chars.
 */
export const computeComplianceContentHash = async (subject, body) => {
  // Browser-only utility. `window.crypto.subtle` is available in every
  // target browser and in jsdom >= 22 for tests. Guarded so callers
  // that catch the rejection can fall back to server-stamped staleness.
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : undefined;
  if (!subtle || typeof subtle.digest !== 'function') {
    throw new Error('Web Crypto SubtleCrypto.digest is not available');
  }
  const canonical = `${toCanonicalString(subject)}\n${toCanonicalString(body)}`;
  const encoded = new TextEncoder().encode(canonical);
  const digest = await subtle.digest('SHA-256', encoded);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

export default computeComplianceContentHash;
